import { Box, Divider, Icon, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconChevronRightFilled, IconUserFilled } from '@zillow/constellation-icons';
import { ChatAvatar, chatBubbleStyles, defaultChatBubbleStyles } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import { RENTER_PROFILE_CHAT_BUBBLE_ABOUT_ME_CLICK_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/renterProfile';
import useDevices from '../../hooks/useDevices';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';
import { getFormattedAttributesList } from '../../utils/renterProfileUtils';

const MAX_SIZE_ATTRIBUTES_LIST = 3;

export type RenterProfileInlineData = {
  senderName?: string;
  attributes: ConversationMessagesData['renterProfile']['attributes'];
  updatedAtMs?: number;
};

export const selectRenterProfileInlineData = (data: ConversationMessagesData): RenterProfileInlineData => ({
  senderName: data.conversation[0]?.senderName,
  attributes: data.renterProfile.attributes,
  updatedAtMs: data.renterProfile.attributes?.updatedAtMs,
});

export type RenterProfileChatBubbleProps = {
  shouldShowViewRenterProfileButton?: boolean;
  conversationId: string | undefined;
  clientTimeZone: string;
  onRenterProfileClick: () => void;
};

export default function RenterProfileChatBubble({
  conversationId,
  clientTimeZone,
  onRenterProfileClick,
  shouldShowViewRenterProfileButton = false,
}: RenterProfileChatBubbleProps): JSX.Element | null {
  const { isMobileView } = useDevices();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  // `conversationId`/`clientTimeZone` come in as props (not `InboxPageContext`) so the bubble
  // works on both the inbox and the lead-management `RenterMessagingModal` surfaces.
  const { data } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectRenterProfileInlineData,
  });

  if (!data) {
    return null;
  }

  const { senderName, attributes, updatedAtMs } = data;
  const formattedAttributesList = getFormattedAttributesList(attributes);

  if (!formattedAttributesList.length) {
    return null;
  }

  const formattedTimestamp = updatedAtMs ? `as of ${format(new Date(updatedAtMs), 'MMM d, h:mm a')}` : '';

  const handleAboutMeClick = (): void => {
    onRenterProfileClick();
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'row', width: '100%', marginBlock: 'default', marginRight: 'auto' }}>
      <ChatAvatar hasTimestampInMessage={false} senderFullName={senderName} type="user" />

      <Box css={{ ...chatBubbleStyles, ...defaultChatBubbleStyles, padding: 0 }}>
        <Box css={{ display: 'flex', alignItems: 'center', padding: 'default' }}>
          <Icon size="md" css={{ color: 'text.subtle', marginRight: 'default' }}>
            <IconUserFilled />
          </Icon>

          <Box>
            <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle' }}>
              About me
            </Paragraph>

            <Text asChild textStyle="body-xs">
              <time>{formattedTimestamp}</time>
            </Text>
          </Box>

          {(isMobileView || shouldShowViewRenterProfileButton) && (
            <IconButton
              data-eventid={RENTER_PROFILE_CHAT_BUBBLE_ABOUT_ME_CLICK_EVENT_TYPE_ID}
              emphasis="bare"
              icon={<IconChevronRightFilled />}
              onClick={handleAboutMeClick}
              shape="circle"
              size="sm"
              title="View renter profile"
              tone="neutral"
              css={{ marginLeft: 'default' }}
            />
          )}
        </Box>

        <Divider />

        <Box css={{ padding: 'default' }}>
          {formattedAttributesList.map((attribute, idx) => {
            if (idx < MAX_SIZE_ATTRIBUTES_LIST) {
              return (
                <div key={`attribute-${idx}`} data-test="renter-profile-inline-attribute">
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    {attribute.title}
                  </Paragraph>

                  <Paragraph textStyle="body-sm" css={{ marginBottom: 'tighter' }}>
                    {attribute.value}
                  </Paragraph>
                </div>
              );
            }
          })}

          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}>
            {isMobileView ? 'More...' : 'See more in Renter Profile...'}
          </Paragraph>
        </Box>
      </Box>
    </Box>
  );
}
